import { useContext } from 'react';
import { GlobalContext } from '../../../../library/Core/GlobalContext';
import { EntrepreneurAnalysisData, EntrepreneurFunnelKey, GenerationRow } from '../types';
import { formatNum } from '../chartSetup';
import { countLink, mutedLabel, neutralPill } from './styles';
import { buildAlumniDirectoryPath, buildEntrepreneurDirectoryPath } from '../directoryLinks';
import SectionCard from './SectionCard';
import SectionStateCard from './SectionStateCard';
import CenteredFunnel from './CenteredFunnel';

interface Props {
    data: EntrepreneurAnalysisData | null;
    error?: string;
    emptyMessage?: string;
    campusId?: string | null;
    enableDirectoryLinks?: boolean;
}

const FIRST_GENERATION = 'FIRST_GENERATION';
const FAMILY_MANAGED = 'FAMILY_MANAGED';
const JOB_FOUNDER_STATUS_DOMAIN = 'JOB_FOUNDER_STATUS';
const COMPANY_ANNUAL_REVENUE_DOMAIN = 'COMPANY_ANNUAL_REVENUE';

const FUNNEL_LABELS: Record<string, string> = {
    totalAlumni: 'Total Alumni on Platform',
    claimedEntrepreneurs: 'Found/Claim to be Entrepreneurs',
    confirmedEntrepreneurs: 'Entrepreneurship Status Confirmed',
    withFinancialData: 'Companies with Financial Data',
};

const funnelHref = (key: string | undefined, campusId?: string | null): string | null => {
    if (key === 'totalAlumni') return buildAlumniDirectoryPath({ instituteId: campusId });
    if (key === 'claimedEntrepreneurs') {
        return buildEntrepreneurDirectoryPath({ selectedCard: 'claimedEntrepreneurs', instituteId: campusId });
    }
    if (key === 'confirmedEntrepreneurs') {
        return buildEntrepreneurDirectoryPath({ selectedCard: 'confirmedEntrepreneurs', instituteId: campusId });
    }
    if (key === 'withFinancialData') {
        return buildEntrepreneurDirectoryPath({ selectedCard: 'withFinancialData', instituteId: campusId });
    }
    return null;
};

const funnelKeyFromStage = (stage: string): EntrepreneurFunnelKey | undefined => {
    const s = stage.toLowerCase();
    if (s.includes('total alumni')) return 'totalAlumni';
    if (s.includes('claim')) return 'claimedEntrepreneurs';
    if (s.includes('confirmed')) return 'confirmedEntrepreneurs';
    if (s.includes('financial')) return 'withFinancialData';
    return undefined;
};

const generationCode = (row: GenerationRow): string | null => {
    if (row.code) return String(row.code);
    return null;
};

const CountValue = ({
    count,
    href,
    enableDirectoryLinks,
}: {
    count: number;
    href: string | null;
    enableDirectoryLinks?: boolean;
}) =>
    enableDirectoryLinks && href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={countLink} title="Open Entrepreneur Directory">
            {formatNum(count)}
        </a>
    ) : (
        <span style={neutralPill}>{formatNum(count)}</span>
    );

const GenerationCard = ({
    title,
    rows,
    enableDirectoryLinks,
    hrefFor,
}: {
    title: string;
    rows: GenerationRow[];
    enableDirectoryLinks?: boolean;
    hrefFor: (row: GenerationRow) => string | null;
}) => (
    <div className="p-3 rounded h-100" style={{ background: '#f7f9fb' }}>
        <div className="mb-2" style={mutedLabel}>
            {title}
        </div>
        {rows.length === 0 ? (
            <span className="font-12 text-muted">—</span>
        ) : (
            rows.map((r) => (
                <div key={r.code || r.label} className="d-flex justify-content-between align-items-center py-1">
                    <span className="font-13" style={{ color: '#6b7785' }}>
                        {r.label}
                    </span>
                    <CountValue count={r.count} href={hrefFor(r)} enableDirectoryLinks={enableDirectoryLinks} />
                </div>
            ))
        )}
    </div>
);

const topRevenueTitle = (count: number) =>
    count > 0 ? `Top ${count} Revenue Entrepreneurs` : 'Top Revenue Entrepreneurs';

const EntrepreneurAnalysisSection = ({
    data,
    error,
    emptyMessage,
    campusId,
    enableDirectoryLinks = false,
}: Props) => {
    const { globalState } = useContext(GlobalContext);
    const domainLabel = (category: string, code: string | null | undefined, fallback = '') => {
        if (!code) return fallback;
        const items = globalState?.domain?.get(category) || [];
        const hit = items.find((el: any) => String(el.code) === String(code));
        return hit?.value || fallback || String(code);
    };
    const revenueLabel = (code: string, fallback: string) =>
        domainLabel(COMPANY_ANNUAL_REVENUE_DOMAIN, code, fallback);
    const founderLabel = (row: GenerationRow) =>
        domainLabel(JOB_FOUNDER_STATUS_DOMAIN, row.code, row.label || '');
    const domainOrder = (category: string, code?: string | null) => {
        const items = globalState?.domain?.get(category) || [];
        const hit = items.find((el: any) => String(el.code) === String(code || ''));
        return hit?.displayOrder ?? 999;
    };
    const revenueRows = [...(data?.revenueBreakup || [])].sort(
        (a, b) =>
            domainOrder(COMPANY_ANNUAL_REVENUE_DOMAIN, a.code || a.threshold) -
            domainOrder(COMPANY_ANNUAL_REVENUE_DOMAIN, b.code || b.threshold)
    );
    const generationRows = (rows: GenerationRow[] = []) =>
        [...rows]
            .sort((a, b) => domainOrder(JOB_FOUNDER_STATUS_DOMAIN, a.code) - domainOrder(JOB_FOUNDER_STATUS_DOMAIN, b.code))
            .map((r) => ({ ...r, label: founderLabel(r) }));

    if (error) {
        return (
            <SectionCard title="Entrepreneur Alumni Analysis">
                <SectionStateCard variant="error" message={error} />
            </SectionCard>
        );
    }
    if (!data) {
        return (
            <SectionCard title="Entrepreneur Alumni Analysis">
                <SectionStateCard variant="empty" message={emptyMessage} />
            </SectionCard>
        );
    }

    const funnelItems = data.funnel.map((s) => {
        const key = s.key || funnelKeyFromStage(s.stage || '');
        return {
            label: (key && FUNNEL_LABELS[key]) || s.stage || key || '',
            count: s.count,
            href: enableDirectoryLinks ? funnelHref(key, campusId) : null,
        };
    });

    const topRevenueCount = data.generationSplit.top15.reduce((sum, r) => sum + (r.count || 0), 0);
    const showTop15 = topRevenueCount > 0;

    const allHref = (row: GenerationRow) => {
        const code = generationCode(row);
        if (code === FIRST_GENERATION) {
            return buildEntrepreneurDirectoryPath({ isFirstGeneration: true, instituteId: campusId });
        }
        if (code === FAMILY_MANAGED) {
            return buildEntrepreneurDirectoryPath({ isFamilyManaged: true, instituteId: campusId });
        }
        return null;
    };

    const top15Href = (row: GenerationRow) => {
        const code = generationCode(row);
        if (code === FIRST_GENERATION) {
            return buildEntrepreneurDirectoryPath({
                topRevenueEntrepreneurs: true,
                isFirstGeneration: true,
                instituteId: campusId,
            });
        }
        if (code === FAMILY_MANAGED) {
            return buildEntrepreneurDirectoryPath({
                topRevenueEntrepreneurs: true,
                isFamilyManaged: true,
                instituteId: campusId,
            });
        }
        return null;
    };

    return (
        <SectionCard title="Entrepreneur Alumni Analysis">
            <div className="row g-4">
                <div className="col-12 col-lg-6">
                    <div className="mb-3" style={mutedLabel}>
                        Funnel
                    </div>
                    {funnelItems.length === 0 ? (
                        <SectionStateCard variant="empty" />
                    ) : (
                        <CenteredFunnel items={funnelItems} labelWidth={42} minWidth={45} maxWidth={92} />
                    )}
                </div>

                <div className="col-12 col-lg-6">
                    <div className="mb-3" style={mutedLabel}>
                        Annual Revenue Breakup (₹)
                    </div>
                    <div className="mb-4">
                        <div
                            className="d-flex align-items-center px-3 py-2 mb-1"
                            style={{ background: '#f7f9fb', borderRadius: 6 }}>
                            <div style={{ flex: 1, ...mutedLabel }}>Revenue Threshold</div>
                            <div style={{ ...mutedLabel }}># Alumni</div>
                        </div>
                        {revenueRows.length === 0 ? (
                            <div className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom">
                                <span className="font-14" style={{ color: '#6b7785' }}>—</span>
                                <span style={neutralPill}>0</span>
                            </div>
                        ) : (
                            revenueRows.map((r) => {
                                const band = r.code || r.threshold;
                                const href = band
                                    ? buildEntrepreneurDirectoryPath({
                                          revenueBand: band,
                                          selectedCard: 'confirmedEntrepreneurs',
                                          instituteId: campusId,
                                      })
                                    : null;
                                return (
                                    <div
                                        key={band}
                                        className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                                        <span className="font-14">{revenueLabel(String(band || ''), r.threshold || '')}</span>
                                        <CountValue
                                            count={r.count}
                                            href={href}
                                            enableDirectoryLinks={enableDirectoryLinks}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="row g-3">
                        {showTop15 && (
                            <div className="col-6">
                                <GenerationCard
                                    title={topRevenueTitle(topRevenueCount)}
                                    rows={generationRows(data.generationSplit.top15)}
                                    enableDirectoryLinks={enableDirectoryLinks}
                                    hrefFor={top15Href}
                                />
                            </div>
                        )}
                        <div className={showTop15 ? 'col-6' : 'col-12'}>
                            <GenerationCard
                                title="All Entrepreneurs"
                                rows={generationRows(data.generationSplit.all)}
                                enableDirectoryLinks={enableDirectoryLinks}
                                hrefFor={allHref}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionCard>
    );
};

export default EntrepreneurAnalysisSection;
